import { FunctionComponent, memo, useEffect, useState } from 'react';

// NPM
import classnames from 'classnames';

// Contexts
import { ViewMedia, useViewDetected } from '@Components/Utils/ViewDetected';

// Libs
import { useHttpContext, getHttpErrorMessage } from '@Libs/http';

// API
import newsFeedsdApi from '@Libs/api/cms/newsFeed';

//@Components
import Anchor from '@Components/Utils/Anchor';
import { useAppStore } from '@Components/AppStore/Context';
import { useAlertContext } from '@Components/Alert/Context';
import { useLayoutContext } from '@Components/Layout/Contexts';

// Components
import Masonry from '@Components/UI/Masonry';
import AnswerCard from '@Pages/hoi-dap/Components/UI/AnswerCard';

// Styles
import styles from './QuestionRelated.module.scss';

// Types
import Newsfeed from 'types/NewsFeed';
interface QuestionRelatedProps {
    slug?: string;
    className?: string;
}

const LIMIT = 10;

const QuestionRelated: FunctionComponent<QuestionRelatedProps> = memo(({ className, slug }) => {
    const { deviceType } = useViewDetected();
    const { onScroll } = useLayoutContext();

    // use context
    const { alert } = useAlertContext();
    const { setLoading } = useAppStore();
    const { cmsHttp } = useHttpContext();

    // Declare
    const isMobile = deviceType !== 'desktop';

    // classnames
    const wrapperClassName = classnames([styles.wrapper, { [styles.mobile]: isMobile }, className]);

    // useState
    const [startIndex, setStartIndex] = useState<number>(0);
    const [isLoadMore, setIsLoadMore] = useState<boolean>(false);
    const [relateLists, setRelatedLists] = useState<Array<Newsfeed>>([]);

    // useEffect
    useEffect(() => {
        (async () => {
            const newsfeeds = await getRelated(slug);
            setRelatedLists(newsfeeds);
            setIsLoadMore(newsfeeds?.length < LIMIT);
        })();
    }, [slug]);

    const getRelated = async (slug, startIndex = 0) => {
        try {
            setLoading(true);
            const response = await newsFeedsdApi(cmsHttp).getRelated({ id: slug, startIndex });
            return response?.newsfeeds;
        } catch (error) {
            alert({
                type: 'error',
                title: 'Đã xảy ra lỗi',
                text: getHttpErrorMessage(error),
            });
        } finally {
            setLoading(false);
        }
    };

    onScroll(
        (event, { isBottom }) => {
            if (isBottom && isMobile) {
                isLoadMore && handleLoadMore();
            }
        },
        [relateLists?.length],
    );

    const handleLoadMore = async () => {
        if (isLoadMore) return;
        const data = await getRelated(slug, startIndex + LIMIT);
        setStartIndex(startIndex);
        setIsLoadMore(data?.length < LIMIT);
        setRelatedLists([...relateLists, ...data]);
    };

    return (
        <div className={wrapperClassName}>
            {relateLists?.length > 0 && (
                <>
                    <div className={styles.title}>Câu hỏi liên quan</div>
                    <Masonry colsNumber={isMobile ? 1 : 2} className={styles.masonry} columnClassName={styles.col}>
                        {relateLists.map((newsfeed) => {
                            // Declare
                            const slug = newsfeed?.slug;
                            const attachmentImages = newsfeed?.attachment?.url;

                            return (
                                <Anchor
                                    href={`/hoi-dap/${slug}`}
                                    className={styles.card_item}
                                    key={`related_question_${slug}`}
                                >
                                    <AnswerCard
                                        image={attachmentImages}
                                        className={styles.card_item}
                                        content={newsfeed?.question?.content || ''}
                                        title={attachmentImages ? newsfeed?.title : ''}
                                        doctorProfile={newsfeed?.question?.questionComment?.doctor}
                                    />
                                </Anchor>
                            );
                        })}
                    </Masonry>
                    {!isLoadMore && (
                        <ViewMedia is="desktop">
                            <div className={styles.btn_button} onClick={handleLoadMore}>
                                <span>Xem thêm</span>
                            </div>
                        </ViewMedia>
                    )}
                </>
            )}
        </div>
    );
});

QuestionRelated.defaultProps = {
    className: '',
};

export default QuestionRelated;
